import { useMutation } from "@apollo/client";
import { TrashIcon, PencilIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { UserGroupIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import { toast } from "react-hot-toast";
import {
  DeleteUserGroupDocument,
  UserGroupFragment,
  UserGroupsDocument,
} from "../../../../../../../../__generated__/graphql";
import Avatar from "../../../../../../../../components/Avatar";
import Button from "../../../../../../../../components/Button";
import ConfirmDialog from "../../../../../../../../components/ConfirmDialog";
import UserGroupModal from "../UserGroupModal";

interface UserGroupCardProps {
  userGroup: UserGroupFragment;
  isAssigned?: boolean;
  onAssign?: () => void;
  onUnassign?: () => void;
  showActions?: boolean;
  canEdit?: boolean;
  projectId?: string;
  eventId?: string;
  onGroupUpdated?: () => void;
}

export default function UserGroupCard({
  userGroup,
  isAssigned,
  onAssign,
  onUnassign,
  showActions = true,
  canEdit = true,
  projectId,
  eventId,
  onGroupUpdated,
}: UserGroupCardProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showConfirmUnassign, setShowConfirmUnassign] = useState(false);

  const [deleteUserGroup, { loading: isDeleting }] = useMutation(
    DeleteUserGroupDocument,
    {
      refetchQueries: [UserGroupsDocument],
    }
  );

  const handleDelete = async () => {
    try {
      await deleteUserGroup({
        variables: {
          userGroupId: userGroup.id,
        },
      });
      toast.success("Groupe supprimé avec succès");
      setShowConfirmDelete(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Erreur lors de la suppression"
      );
    }
  };

  return (
    <>
      <UserGroupModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        userGroup={userGroup}
        projectId={projectId}
        eventId={eventId}
        onGroupUpdated={onGroupUpdated}
      />

      <ConfirmDialog
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={handleDelete}
        title="Supprimer le groupe"
        description={`Êtes-vous sûr de vouloir supprimer le groupe "${userGroup.name}" ? Cette action est irréversible.`}
        confirmText="Supprimer"
        cancelText="Annuler"
        isLoading={isDeleting}
        variant="danger"
      />

      <ConfirmDialog
        isOpen={showConfirmUnassign}
        onClose={() => setShowConfirmUnassign(false)}
        onConfirm={() => {
          setShowConfirmUnassign(false);
          onUnassign?.();
        }}
        title="Retirer le groupe du projet"
        description={`Êtes-vous sûr de vouloir retirer le groupe "${userGroup.name}" du projet ? Les membres de ce groupe perdront l'accès aux évènements, activités et documents liés à ce groupe.`}
        confirmText="Retirer le groupe"
        cancelText="Annuler"
        variant="danger"
      />

      <div className="bg-card border border-border rounded-lg p-4 flex flex-col gap-3 h-full">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
              <UserGroupIcon className="w-5 h-5 text-accent" />
            </div>
            <div>
              <div className="font-bold text-primary">{userGroup.name}</div>
              {userGroup.description && (
                <div className="text-xs text-secondary">
                  {userGroup.description}
                </div>
              )}
            </div>
          </div>

          {showActions && canEdit && (
            <div className="flex items-center gap-1">
              <button
                className="p-1 hover:bg-border rounded text-secondary hover:text-primary transition-colors"
                onClick={() => setIsEditModalOpen(true)}
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                className="p-1 hover:bg-red-500/20 rounded text-secondary hover:text-red-500 transition-colors"
                onClick={() => setShowConfirmDelete(true)}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="text-xs text-secondary">
          {userGroup.members.length} membre
          {userGroup.members.length > 1 ? "s" : ""}
          {!canEdit && userGroup.createdByPublicUserOrganization && (
            <span className="ml-1 text-tertiary">
              · Créé par {userGroup.createdByPublicUserOrganization.name}
            </span>
          )}
        </div>

        {userGroup.members.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {userGroup.members.slice(0, 5).map((member) => (
              <div
                key={member.id}
                className="flex items-center gap-1 bg-dark rounded-full px-2 py-1"
              >
                <Avatar
                  userOrganization={member.publicUserOrganization}
                  size="xs"
                />
                <span className="text-xs text-primary truncate max-w-[100px]">
                  {member.publicUserOrganization.name}
                </span>
              </div>
            ))}
            {userGroup.members.length > 5 && (
              <div className="flex items-center text-xs text-tertiary">
                +{userGroup.members.length - 5} autres
              </div>
            )}
          </div>
        )}

        {(onAssign || onUnassign) && (
          <div className="pt-2 border-t border-border mt-auto">
            {isAssigned ? (
              <Button
                variant="secondary"
                className="w-full text-red-400 hover:text-red-300 gap-2"
                onClick={() => setShowConfirmUnassign(true)}
              >
                <XMarkIcon className="w-4 h-4" />
                Retirer du projet
              </Button>
            ) : (
              <Button
                variant="secondary"
                className="w-full gap-2"
                onClick={onAssign}
              >
                Assigner au projet
              </Button>
            )}
          </div>
        )}
      </div>
    </>
  );
}
